/**
 * Faceted filter-option computation for the History tab.
 *
 * Each dropdown lists only the values present in the records that would be
 * listed given the currently-active filters EXCEPT the dropdown's own dimension.
 * Excluding the own dimension keeps every dropdown populated with its real
 * alternatives (selecting Carrier=X must not collapse the Carrier list to just
 * X), while still guaranteeing every offered selection exists in the listed
 * records.
 */
import { UnscheduledEvent, FilterOptions } from '../types';
import {
  collectIropFilterOptions,
  eventHasIropCode,
  isIropCode,
  IropFilterOption,
} from './iropUtils';

/**
 * Returns the unique, non-empty ATA chapters associated with an event. Chapters
 * come from per-defect classifications (`selectedChapters`) when present, with a
 * fallback to the event-level `ataChapter`. Used to build the History ATA Code
 * filter options and to match events against the selected chapter.
 */
export function eventAtaChapters(event: UnscheduledEvent): string[] {
  const chapters = new Set<string>();
  if (event.selectedChapters) {
    for (const chapter of Object.values(event.selectedChapters)) {
      if (chapter) chapters.add(chapter);
    }
  }
  if (event.ataChapter) chapters.add(event.ataChapter);
  return Array.from(chapters);
}

/** The categorical History filter dimensions used for faceted option lists. */
type Dimension = 'eventType' | 'carrier' | 'aircraft' | 'station' | 'ataChapter' | 'irop';

const DIMENSIONS: readonly Dimension[] = [
  'eventType',
  'carrier',
  'aircraft',
  'station',
  'ataChapter',
  'irop',
];

/** Whether an event satisfies a single categorical History filter dimension. */
function passesDimension(
  event: UnscheduledEvent,
  dimension: Dimension,
  filters: FilterOptions,
): boolean {
  switch (dimension) {
    case 'eventType':
      return !filters.eventType || (event.recordType || 'Unscheduled') === filters.eventType;
    case 'carrier':
      return !filters.carrier || event.carrier === filters.carrier;
    case 'aircraft':
      return !filters.aircraft || event.tailNumber === filters.aircraft;
    case 'station':
      return !filters.station || event.station === filters.station;
    case 'ataChapter':
      return !filters.ataChapter || eventAtaChapters(event).includes(filters.ataChapter);
    case 'irop':
      return !filters.irop || !isIropCode(filters.irop) || eventHasIropCode(event, filters.irop);
    default:
      return true;
  }
}

/** Events passing every categorical filter except the excluded dimension. */
function eventsExcluding(
  events: UnscheduledEvent[],
  filters: FilterOptions,
  exclude: Dimension,
): UnscheduledEvent[] {
  return events.filter((event) =>
    DIMENSIONS.every(
      (dimension) => dimension === exclude || passesDimension(event, dimension, filters),
    ),
  );
}

/** Distinct, non-empty, sorted values from `events` via `accessor`. */
function uniqueSorted(
  events: UnscheduledEvent[],
  accessor: (event: UnscheduledEvent) => string,
): string[] {
  return Array.from(new Set(events.map(accessor).filter(Boolean))).sort();
}

/** The set of option lists backing the History filter dropdowns. */
export interface HistoryFilterOptions {
  recordTypes: string[];
  carriers: string[];
  tailNumbers: string[];
  stations: string[];
  ataChapters: string[];
  iropOptions: IropFilterOption[];
}

/**
 * Computes the faceted History filter options from the events within the
 * selected window. Each list is derived from the records passing the other
 * active filters, so a dropdown only ever offers selections that exist in the
 * records currently listed.
 */
export function computeHistoryFilterOptions(
  windowEvents: UnscheduledEvent[],
  filters: FilterOptions,
): HistoryFilterOptions {
  return {
    recordTypes: uniqueSorted(
      eventsExcluding(windowEvents, filters, 'eventType'),
      (e) => e.recordType || 'Unscheduled',
    ),
    carriers: uniqueSorted(eventsExcluding(windowEvents, filters, 'carrier'), (e) => e.carrier),
    tailNumbers: uniqueSorted(
      eventsExcluding(windowEvents, filters, 'aircraft'),
      (e) => e.tailNumber,
    ),
    stations: uniqueSorted(eventsExcluding(windowEvents, filters, 'station'), (e) => e.station),
    ataChapters: Array.from(
      new Set(
        eventsExcluding(windowEvents, filters, 'ataChapter').flatMap((e) => eventAtaChapters(e)),
      ),
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    iropOptions: collectIropFilterOptions(eventsExcluding(windowEvents, filters, 'irop')),
  };
}
